import type { DatasetPlanPositioningEditorProps } from "./dataset-plan-positioning-editor.type";
import type {
  DatasetPlanPositioningData,
  DatasetPlanPositioning,
} from "@/entities/dataset";
import type { SchemaDatasetPlanPositioning } from "@/entities/schema";
import type { PositionPlanPositioningProps } from "@/modules/position-plan-positioning";

import { DATASET_TYPE } from "@/entities/dataset";
import { useDatasetsMutatorSync } from "@/modules/datasets/datasets-mutator";

import { useDatasetPlanPositioningContext } from "../dataset-plan-positioning.context";

const datasetType = DATASET_TYPE.PLAN_POSITIONING;

export const useDatasetPlanPositioningEditor = ({
  template,
  planUrl,
  toolbarOptions,
  fitToWrapper,
}: DatasetPlanPositioningEditorProps) => {
  const { coordinates, setCoordinates, page, setPage, isEditable } =
    useDatasetPlanPositioningContext();

  const { handleMutation } = useDatasetPlanPositioningMutator(template);

  const onCoordinatesChange = (newCoordinates: DatasetPlanPositioningData) => {
    setCoordinates(newCoordinates);
    handleMutation(newCoordinates);
  };

  const positionProps: PositionPlanPositioningProps = {
    planSourceUrl: planUrl,
    controlledCoordinates: coordinates,
    controlledPage: page,
    isEditable,
    onCoordinatesChange,
    onPageChange: setPage,
    toolbarOptions,
    fitToWrapper,
  };

  return {
    positionProps,
  };
};

export const useDatasetPlanPositioningMutator = (
  template: SchemaDatasetPlanPositioning
) => {
  const { dataset, asset } = useDatasetPlanPositioningContext();
  const { addMutatorItem } = useDatasetsMutatorSync();

  const handleMutation = (newCoordinates: DatasetPlanPositioningData) => {
    if (dataset) {
      const variables: Partial<DatasetPlanPositioning> = {
        data: newCoordinates,
      };
      addMutatorItem({
        mutationType: "update",
        datasetType,
        datasetId: dataset.id,
        variables,
      });
    } else {
      const variables: Partial<DatasetPlanPositioning> = {
        name: template.name ?? "",
        type: datasetType,
        data: newCoordinates,
        source: asset?.plan_file?.path,
        layout_index: template.layout_index,
      };
      addMutatorItem({
        mutationType: "create",
        datasetType,
        variables,
      });
    }
  };

  return {
    handleMutation,
  };
};
